import React, { useCallback, useRef, useState, useEffect, useMemo } from "react";
import { useLocation, useHistory } from "react-router-dom";
import {
  downloadFlowOfFundsCsv,
  FlowOfFundsFilterParams,
  getFlowOfFunds,
  getFlowOfFundsTagLevel,
  listAccounts,
  SUGGESTED_ACCOUNT_ATTRIBUTE_KEYS,
} from "../../../../docuclipper/api";
import { useDispatch, useSelector } from "react-redux";
import { ReduxState } from "../../../../redux";
import { createAlert } from "src/redux/reducers/Alerts";
import { FlowOfFundsChart } from "./FlowOfFundsChart";
import { FlowOfFundsTable } from "./FlowOfFundsTable";
import type { ChartFilterChange, SankeyData } from "./FlowOfFundsChart";

function reorganizeNodes(data: any): SankeyData {
  if (!data?.nodes?.length || !Array.isArray(data.links)) {
    return { nodes: [], links: [] };
  }

  const valueByIndex: Record<number, number> = {};
  data.nodes.forEach((_: any, i: number) => (valueByIndex[i] = 0));
  data.links.forEach((link: any) => {
    if (
      link.source != null &&
      link.target != null &&
      data.nodes[link.source] &&
      data.nodes[link.target]
    ) {
      const val = Math.abs(Number(link.value) || 0);
      valueByIndex[link.source] = (valueByIndex[link.source] || 0) + val;
      valueByIndex[link.target] = (valueByIndex[link.target] || 0) + val;
    }
  });

  const incomeNodes = data.nodes
    .map((node: any, index: number) => ({
      node,
      index,
      value: valueByIndex[index] || 0,
    }))
    .filter(({ node }: any) => node.category === "income")
    .sort((a: any, b: any) => b.value - a.value)
    .map(({ node }: any) => node);
  const accountNodes = data.nodes.filter((n: any) => n.category === "account");
  const expenseNodes = data.nodes
    .map((node: any, index: number) => ({
      node,
      index,
      value: valueByIndex[index] || 0,
    }))
    .filter(({ node }: any) => node.category === "expense")
    .sort((a: any, b: any) => b.value - a.value)
    .map(({ node }: any) => node);

  const orderedNodes = [...incomeNodes, ...accountNodes, ...expenseNodes].map(
    (node: any) => ({
      ...node,
      x:
        node.category === "income"
          ? 20
          : node.category === "expense"
            ? 840
            : 400,
    }),
  );

  const nodeIndexMap = Object.fromEntries(
    orderedNodes.map((node: any, index: number) => [node.name, index]),
  );

  const updatedLinks = data.links
    .filter(
      (link: any) =>
        link.source != null &&
        link.target != null &&
        data.nodes[link.source] &&
        data.nodes[link.target],
    )
    .map((link: any) => ({
      ...link,
      source: nodeIndexMap[data.nodes[link.source].name],
      target: nodeIndexMap[data.nodes[link.target].name],
    }));

  return { nodes: orderedNodes, links: updatedLinks };
}

// ---------------------------------------------------------------------------
// URL <-> filter shape. Filters live in the URL query string so the view is
// shareable / bookmarkable. We parse/serialize manually (no qs dep) because
// the shape is small and the rest of the UI doesn't pull qs in.
//
// URL params:
//   filter[<key>]=<value>   attribute predicate (repeated for multi-value)
//   accountIds=1,2,3        explicit Account.id picks (from top-bar dropdown
//                           or from clicking an account node on the chart and
//                           resolving via accounts list)
//   selectedAccountName=X   chart-side account scoping when no id mapping
//                           exists (i.e. clicked an account node whose name
//                           is not in the accounts list). Applied as a
//                           TL.account equality on the table fetch.
//   source=<nodeName>       chart-link / chart-node scoping: only TLs whose
//   dest=<nodeName>         chart-side (source,dest) match these node names.
// ---------------------------------------------------------------------------

export interface FlowOfFundsUrlFilters extends FlowOfFundsFilterParams {
  selectedAccountName?: string;
  source?: string;
  dest?: string;
}

function parseFiltersFromSearch(search: string): FlowOfFundsUrlFilters {
  const params = new URLSearchParams(search);
  const attributes: Record<string, string[]> = {};
  params.forEach((value, key) => {
    const m = key.match(/^filter\[(.+)\]$/);
    if (!m) return;
    const attrKey = m[1];
    if (!attributes[attrKey]) attributes[attrKey] = [];
    if (value) attributes[attrKey].push(value);
  });
  const accountIds: string[] = [];
  const idsRaw = params.get("accountIds");
  if (idsRaw) {
    for (const id of idsRaw.split(",")) {
      const t = id.trim();
      if (t) accountIds.push(t);
    }
  }
  const out: FlowOfFundsUrlFilters = {};
  const normAttrs: Record<string, string | string[]> = {};
  for (const k of Object.keys(attributes)) {
    normAttrs[k] = attributes[k].length === 1 ? attributes[k][0] : attributes[k];
  }
  if (Object.keys(normAttrs).length) out.attributes = normAttrs;
  if (accountIds.length) out.accountIds = accountIds;
  const selectedAccountName = params.get("selectedAccountName");
  if (selectedAccountName) out.selectedAccountName = selectedAccountName;
  const source = params.get("source");
  if (source) out.source = source;
  const dest = params.get("dest");
  if (dest) out.dest = dest;
  return out;
}

function serializeFiltersToSearch(
  prevSearch: string,
  filters: FlowOfFundsUrlFilters,
): string {
  // Preserve any non-filter params (l1, l2, etc).
  const params = new URLSearchParams(prevSearch);
  // Strip existing filter[*] + accountIds + selectedAccountName + source/dest.
  const toDrop: string[] = [];
  params.forEach((_, key) => {
    if (
      key.startsWith("filter[") ||
      key === "accountIds" ||
      key === "selectedAccountName" ||
      key === "source" ||
      key === "dest"
    ) {
      toDrop.push(key);
    }
  });
  toDrop.forEach((k) => params.delete(k));
  if (filters.attributes) {
    for (const k of Object.keys(filters.attributes)) {
      const v = filters.attributes[k];
      const arr = Array.isArray(v) ? v : [v];
      for (const val of arr) {
        if (val) params.append(`filter[${k}]`, String(val));
      }
    }
  }
  if (filters.accountIds && filters.accountIds.length) {
    params.set("accountIds", filters.accountIds.map(String).join(","));
  }
  if (filters.selectedAccountName) {
    params.set("selectedAccountName", filters.selectedAccountName);
  }
  if (filters.source) params.set("source", filters.source);
  if (filters.dest) params.set("dest", filters.dest);
  const out = params.toString();
  return out ? `?${out}` : "";
}

interface AccountLite {
  id: string;
  displayName: string;
  attributes: Array<{ key: string; value: string }>;
}

// ---------------------------------------------------------------------------

export function FlowOfFunds() {
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const gridApiRef = useRef<any>(null);

  const { job } = useSelector((state: ReduxState) => state.JobData);
  const { selectedL1: l1, selectedL2: l2, tagLevels } = useSelector(
    (state: ReduxState) => state.TagLevel,
  );
  // selectedL1/L2 are project names; the accounts API needs the numeric tagId.
  const l2TagId: number | null = useMemo(() => {
    if (!l1 || !l2) return null;
    const raw = (tagLevels as any)?.root?.[l1]?.[l2]?.metadata?.tagId;
    const n = Number(raw);
    return Number.isFinite(n) ? n : null;
  }, [tagLevels, l1, l2]);

  const [data2, setData2] = useState<SankeyData>({ nodes: [], links: [] });
  const [isLoadingFlowData, setIsLoadingFlowData] = useState(false);
  const [accounts, setAccounts] = useState<AccountLite[]>([]);
  const [csvBusy, setCsvBusy] = useState(false);

  // Read filters off the URL on every render — single source of truth.
  const apiFilters = useMemo<FlowOfFundsUrlFilters>(
    () => parseFiltersFromSearch(location.search),
    [location.search],
  );

  // Chart fetch only sends the attribute + accountIds filters (server-side
  // FoF endpoint does not know about source/dest/selectedAccountName — those
  // are post-hoc table scopes).
  const chartFilters: FlowOfFundsFilterParams = useMemo(
    () => ({
      attributes: apiFilters.attributes,
      accountIds: apiFilters.accountIds,
    }),
    [apiFilters.attributes, apiFilters.accountIds],
  );
  const chartFiltersKey = useMemo(
    () => JSON.stringify(chartFilters),
    [chartFilters],
  );

  useEffect(() => {
    if (!(job && job.id) && !(l1 && l2)) return;
    setIsLoadingFlowData(true);
    const fn =
      l1 && l2
        ? getFlowOfFundsTagLevel(l1, l2, chartFilters)
        : job && job.id
          ? getFlowOfFunds(job.id, chartFilters)
          : Promise.resolve();
    fn.then((data) => {
      setData2(reorganizeNodes(data));
    })
      .catch((err) => {
        dispatch(
          createAlert({
            message:
              err.message || "Error getting flow of funds. Please try again.",
            timeout: 0,
            type: "error",
          }),
        );
      })
      .finally(() => setIsLoadingFlowData(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [job, l1, l2, dispatch, chartFiltersKey]);

  // Load project accounts for the multi-select + the attribute-keys dropdown.
  useEffect(() => {
    if (l2TagId == null) return;
    listAccounts(l2TagId)
      .then((rsp: any) => {
        const rows = Array.isArray(rsp) ? rsp : rsp?.accounts || [];
        const norm: AccountLite[] = rows.map((a: any) => ({
          id: String(a.id),
          displayName: a.displayName,
          attributes: Array.isArray(a.attributes)
            ? a.attributes
            : Object.entries(a.attributes || {}).map(([key, value]) => ({
                key,
                value: String(value),
              })),
        }));
        setAccounts(norm);
      })
      .catch(() => {
        /* non-fatal */
      });
  }, [l2TagId]);

  const attributeKeys = useMemo(() => {
    // Suggested keys first, then any custom keys already in use on this
    // project. Showing suggested even when no account has them yet so users
    // can discover the concept.
    const present = new Set<string>();
    for (const a of accounts) {
      for (const kv of a.attributes) present.add(kv.key);
    }
    const suggested = SUGGESTED_ACCOUNT_ATTRIBUTE_KEYS.filter(
      (k) => !present.has(k),
    );
    const custom = Array.from(present)
      .filter((k) => !SUGGESTED_ACCOUNT_ATTRIBUTE_KEYS.includes(k))
      .sort();
    return [...SUGGESTED_ACCOUNT_ATTRIBUTE_KEYS.filter((k) => present.has(k)), ...custom, ...suggested];
  }, [accounts]);

  const valuesByKey = useMemo(() => {
    const m: Record<string, string[]> = {};
    for (const a of accounts) {
      for (const kv of a.attributes) {
        if (!m[kv.key]) m[kv.key] = [];
        if (!m[kv.key].includes(kv.value)) m[kv.key].push(kv.value);
      }
    }
    for (const k of Object.keys(m)) m[k].sort();
    return m;
  }, [accounts]);

  const pushFilters = useCallback(
    (next: FlowOfFundsUrlFilters) => {
      const newSearch = serializeFiltersToSearch(location.search, next);
      history.replace({ pathname: location.pathname, search: newSearch });
    },
    [history, location.pathname, location.search],
  );

  const addAttributeFilter = useCallback(
    (key: string, value: string) => {
      const next: FlowOfFundsUrlFilters = {
        ...apiFilters,
        attributes: { ...(apiFilters.attributes || {}) },
        accountIds: apiFilters.accountIds ? [...apiFilters.accountIds] : undefined,
      };
      const cur = next.attributes![key];
      if (cur == null) {
        next.attributes![key] = value;
      } else if (Array.isArray(cur)) {
        if (!cur.includes(value)) cur.push(value);
      } else if (cur !== value) {
        next.attributes![key] = [cur, value];
      }
      pushFilters(next);
    },
    [apiFilters, pushFilters],
  );

  const removeAttributeFilter = useCallback(
    (key: string, value: string) => {
      if (!apiFilters.attributes) return;
      const next: FlowOfFundsUrlFilters = {
        ...apiFilters,
        attributes: { ...apiFilters.attributes },
        accountIds: apiFilters.accountIds ? [...apiFilters.accountIds] : undefined,
      };
      const cur = next.attributes![key];
      if (Array.isArray(cur)) {
        const filtered = cur.filter((v) => v !== value);
        if (filtered.length === 0) delete next.attributes![key];
        else if (filtered.length === 1) next.attributes![key] = filtered[0];
        else next.attributes![key] = filtered;
      } else if (cur === value) {
        delete next.attributes![key];
      }
      if (Object.keys(next.attributes!).length === 0) delete next.attributes;
      pushFilters(next);
    },
    [apiFilters, pushFilters],
  );

  const toggleAccountId = useCallback(
    (id: string) => {
      const cur = apiFilters.accountIds || [];
      const isOn = cur.map(String).includes(id);
      const next: FlowOfFundsUrlFilters = {
        ...apiFilters,
        accountIds: isOn ? cur.filter((x) => String(x) !== id) : [...cur, id],
      };
      if (!next.accountIds || next.accountIds.length === 0) delete next.accountIds;
      pushFilters(next);
    },
    [apiFilters, pushFilters],
  );

  const clearSelectedAccountName = useCallback(() => {
    const next: FlowOfFundsUrlFilters = { ...apiFilters };
    delete next.selectedAccountName;
    pushFilters(next);
  }, [apiFilters, pushFilters]);

  const clearSourceDest = useCallback(() => {
    const next: FlowOfFundsUrlFilters = { ...apiFilters };
    delete next.source;
    delete next.dest;
    pushFilters(next);
  }, [apiFilters, pushFilters]);

  const [pendingAttrKey, setPendingAttrKey] = useState<string>("");
  const handleAddAttribute = (value: string) => {
    if (!pendingAttrKey || !value) return;
    addAttributeFilter(pendingAttrKey, value);
    setPendingAttrKey("");
  };

  const handleDownloadCsv = useCallback(async () => {
    setCsvBusy(true);
    try {
      // CSV export only honours attribute + accountIds; source/dest are
      // table-side scoping that the export endpoint doesn't accept today.
      const exportFilters: FlowOfFundsFilterParams = {
        attributes: apiFilters.attributes,
        accountIds: apiFilters.accountIds,
      };
      if (l1 && l2) {
        await downloadFlowOfFundsCsv({ l1, l2, filters: exportFilters });
      } else if (job && job.id) {
        await downloadFlowOfFundsCsv({ jobId: job.id, filters: exportFilters });
      }
    } catch (err: any) {
      dispatch(
        createAlert({
          message: err.message || "Could not export flow of funds",
          timeout: 0,
          type: "error",
        }),
      );
    } finally {
      setCsvBusy(false);
    }
  }, [l1, l2, job, apiFilters.attributes, apiFilters.accountIds, dispatch]);

  // ------- Chart event handler: route every chart click into the URL -------
  const handleChartFilterChange = useCallback(
    (changes: ChartFilterChange) => {
      // Build a next-state from the current URL. We always blow away the
      // previous chart-driven keys (selectedAccountName, source, dest) and
      // re-set them from the incoming change to match the prior "single chart
      // selection" UX. Attribute and accountIds filters from the top bar are
      // preserved.
      const next: FlowOfFundsUrlFilters = {
        attributes: apiFilters.attributes,
        accountIds: apiFilters.accountIds,
      };

      // Account-node click: prefer toggling accountIds if we can match the
      // node name to an Account.displayName. Otherwise fall back to
      // selectedAccountName for raw TL.account scoping.
      const accountNodeName =
        changes.selectedAccount && !changes.linkFilterSource && !changes.linkFilterDest
          ? changes.selectedAccount
          : undefined;
      if (accountNodeName) {
        const match = accounts.find((a) => a.displayName === accountNodeName);
        if (match) {
          // toggle by id
          const cur = next.accountIds || [];
          const isOn = cur.map(String).includes(match.id);
          next.accountIds = isOn
            ? cur.filter((x) => String(x) !== match.id)
            : [...cur, match.id];
          if (next.accountIds.length === 0) delete next.accountIds;
        } else {
          // toggle the name-based fallback
          next.selectedAccountName =
            apiFilters.selectedAccountName === accountNodeName
              ? undefined
              : accountNodeName;
        }
      }

      // Link click: changes.linkFilterSource + linkFilterDest are set.
      if (changes.linkFilterSource && changes.linkFilterDest) {
        // Toggle off if user re-clicked the same link.
        if (
          apiFilters.source === changes.linkFilterSource &&
          apiFilters.dest === changes.linkFilterDest
        ) {
          // leave next.source/dest unset
        } else {
          next.source = changes.linkFilterSource;
          next.dest = changes.linkFilterDest;
        }
      }

      pushFilters(next);
    },
    [accounts, apiFilters, pushFilters],
  );

  // Flat list of active chips for rendering.
  const activeAttrChips = useMemo(() => {
    const out: Array<{ key: string; value: string }> = [];
    if (apiFilters.attributes) {
      for (const k of Object.keys(apiFilters.attributes)) {
        const v = apiFilters.attributes[k];
        const arr = Array.isArray(v) ? v : [v];
        for (const val of arr) out.push({ key: k, value: String(val) });
      }
    }
    return out;
  }, [apiFilters.attributes]);

  const hasAnyFilter =
    activeAttrChips.length > 0 ||
    (apiFilters.accountIds || []).length > 0 ||
    !!apiFilters.selectedAccountName ||
    !!apiFilters.source ||
    !!apiFilters.dest;

  const filterBar = (
    <div
      className="d-flex flex-wrap align-items-center"
      style={{ gap: "0.5rem", margin: "0.5rem 0" }}
    >
      {activeAttrChips.map((chip) => (
        <span
          key={`${chip.key}=${chip.value}`}
          className="badge badge-info"
          style={{ padding: "0.5em 0.75em", fontSize: "0.85em" }}
        >
          {chip.key}: {chip.value}{" "}
          <button
            type="button"
            aria-label={`Remove filter ${chip.key}=${chip.value}`}
            onClick={() => removeAttributeFilter(chip.key, chip.value)}
            style={{
              background: "transparent",
              border: "none",
              color: "inherit",
              marginLeft: "0.25rem",
              cursor: "pointer",
            }}
          >
            ×
          </button>
        </span>
      ))}
      {(apiFilters.accountIds || []).map((id) => {
        const acc = accounts.find((a) => a.id === String(id));
        return (
          <span
            key={`acct-${id}`}
            className="badge badge-secondary"
            style={{ padding: "0.5em 0.75em", fontSize: "0.85em" }}
          >
            account: {acc ? acc.displayName : id}{" "}
            <button
              type="button"
              aria-label={`Remove account ${id}`}
              onClick={() => toggleAccountId(String(id))}
              style={{
                background: "transparent",
                border: "none",
                color: "inherit",
                marginLeft: "0.25rem",
                cursor: "pointer",
              }}
            >
              ×
            </button>
          </span>
        );
      })}
      {apiFilters.selectedAccountName && (
        <span
          className="badge badge-secondary"
          style={{ padding: "0.5em 0.75em", fontSize: "0.85em" }}
        >
          account: {apiFilters.selectedAccountName}{" "}
          <button
            type="button"
            aria-label={`Remove account ${apiFilters.selectedAccountName}`}
            onClick={clearSelectedAccountName}
            style={{
              background: "transparent",
              border: "none",
              color: "inherit",
              marginLeft: "0.25rem",
              cursor: "pointer",
            }}
          >
            ×
          </button>
        </span>
      )}
      {(apiFilters.source || apiFilters.dest) && (
        <span
          className="badge badge-warning"
          style={{ padding: "0.5em 0.75em", fontSize: "0.85em" }}
        >
          {apiFilters.source || "?"} → {apiFilters.dest || "?"}{" "}
          <button
            type="button"
            aria-label="Remove flow filter"
            onClick={clearSourceDest}
            style={{
              background: "transparent",
              border: "none",
              color: "inherit",
              marginLeft: "0.25rem",
              cursor: "pointer",
            }}
          >
            ×
          </button>
        </span>
      )}

      {attributeKeys.length > 0 && (
        <>
          <select
            value={pendingAttrKey}
            onChange={(e) => setPendingAttrKey(e.target.value)}
            className="form-control form-control-sm"
            style={{ width: "auto" }}
            aria-label="Filter by attribute key"
          >
            <option value="">+ filter by…</option>
            {attributeKeys.map((k) => (
              <option key={k} value={k}>
                {k}
              </option>
            ))}
          </select>
          {pendingAttrKey && (
            <select
              defaultValue=""
              onChange={(e) => handleAddAttribute(e.target.value)}
              className="form-control form-control-sm"
              style={{ width: "auto" }}
              aria-label={`Value for ${pendingAttrKey}`}
            >
              <option value="" disabled>
                pick value…
              </option>
              {(valuesByKey[pendingAttrKey] || []).map((v) => (
                <option key={v} value={v}>
                  {v}
                </option>
              ))}
            </select>
          )}
        </>
      )}

      {accounts.length > 0 && (
        <select
          defaultValue=""
          onChange={(e) => {
            if (e.target.value) toggleAccountId(e.target.value);
            e.target.value = "";
          }}
          className="form-control form-control-sm"
          style={{ width: "auto" }}
          aria-label="Filter by account"
        >
          <option value="">+ add account…</option>
          {accounts
            .filter((a) => !(apiFilters.accountIds || []).map(String).includes(a.id))
            .map((a) => (
              <option key={a.id} value={a.id}>
                {a.displayName}
              </option>
            ))}
        </select>
      )}

      <button
        type="button"
        className="btn btn-sm btn-outline-primary ml-auto"
        onClick={handleDownloadCsv}
        disabled={csvBusy}
      >
        {csvBusy ? "Preparing…" : "Export flow of funds"}
      </button>
    </div>
  );

  // When the chart's data is empty AND we're not loading, show the helpful
  // "no categorized transactions" alert. Otherwise we keep both chart+table
  // mounted with whatever the previous data was and overlay a small spinner.
  const hasData =
    (data2.links || []).length > 0 && (data2.nodes || []).length > 0;

  if (!isLoadingFlowData && !hasData) {
    return (
      <>
        {filterBar}
        <div className="my-2 alert alert-warning" role="alert">
          No categorized transactions found
          {hasAnyFilter ? " for the current filters" : ""}
          . Go to the Categorize tab to assign categories to your transactions,
          then return here to see the flow of funds.
        </div>
      </>
    );
  }

  return (
    <div className="sankey-charts">
      <style>{`@keyframes fof-spin { to { transform: rotate(360deg); } }`}</style>
      {filterBar}
      <div style={{ width: "100%", position: "relative" }}>
        {isLoadingFlowData && (
          <div
            aria-label="Loading flow of funds"
            style={{
              position: "absolute",
              inset: 0,
              background: "rgba(255,255,255,0.6)",
              zIndex: 5,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              pointerEvents: "none",
            }}
          >
            <div
              role="status"
              style={{
                width: 32,
                height: 32,
                border: "3px solid #dee2e6",
                borderTopColor: "#0d6efd",
                borderRadius: "50%",
                animation: "fof-spin 0.7s linear infinite",
              }}
            />
          </div>
        )}
        {hasData && (
          <FlowOfFundsChart
            data={data2}
            onChartFilterChange={handleChartFilterChange}
          />
        )}
      </div>

      <div style={{ marginTop: "7.5rem" }}>
        <FlowOfFundsTable
          apiFilters={apiFilters}
          data2={data2}
          gridApiRef={gridApiRef}
        />
      </div>
    </div>
  );
}

export default FlowOfFunds;
